import { z } from 'zod';

/**
 * The canonical production workflow. Stage KEYS are fixed for the whole product so that
 * notification triggers, the Kanban board and "average days per stage" analytics can rely
 * on what a stage means. A shop may rename a stage's label and hide stages it does not
 * use, but it can never invent a key.
 *
 * `cancelled` is a terminal state that sits outside the linear progression.
 */
export const WORKFLOW_STAGES = [
  'booked',
  'showroom',
  'cutting',
  'stitching',
  'handwork',
  'finishing',
  'ironing',
  'qc',
  'ready',
  'delivered',
  'cancelled',
] as const;

export type WorkflowStage = (typeof WORKFLOW_STAGES)[number];

export const workflowStageSchema = z.enum(WORKFLOW_STAGES);

/**
 * The linear progression an item walks, in order. `cancelled` is deliberately absent —
 * it is reachable from any stage and is not part of the sequence.
 */
export const STAGE_SEQUENCE = [
  'booked',
  'showroom',
  'cutting',
  'stitching',
  'handwork',
  'finishing',
  'ironing',
  'qc',
  'ready',
  'delivered',
] as const satisfies readonly WorkflowStage[];

export type ProgressiveStage = (typeof STAGE_SEQUENCE)[number];

/** Ordinal position of every stage. Stages outside the progression sort last. */
export const STAGE_ORDINAL: Readonly<Record<WorkflowStage, number>> = Object.freeze({
  booked: 0,
  showroom: 1,
  cutting: 2,
  stitching: 3,
  handwork: 4,
  finishing: 5,
  ironing: 6,
  qc: 7,
  ready: 8,
  delivered: 9,
  cancelled: 99,
});

export interface StageDefinition {
  /** Immutable key. Never rename this — order history and analytics reference it. */
  readonly key: WorkflowStage;
  /** Position in the linear progression; `cancelled` sorts last. */
  readonly ordinal: number;
  /** Default English label. Shops override it; the UI reads `stage.<key>` from i18next. */
  readonly defaultLabel: string;
  /** Whether the stage is part of the linear progression. */
  readonly progressive: boolean;
  /** Nothing follows a terminal stage. */
  readonly terminal: boolean;
  /** Whether a brand-new shop starts with this stage switched on. */
  readonly defaultEnabled: boolean;
  /** A shop may not hide these — the workflow would have no beginning or end. */
  readonly required: boolean;
}

export const STAGE_DEFINITIONS: Readonly<Record<WorkflowStage, StageDefinition>> = Object.freeze({
  booked: {
    key: 'booked',
    ordinal: 0,
    defaultLabel: 'Booked',
    progressive: true,
    terminal: false,
    defaultEnabled: true,
    required: true,
  },
  showroom: {
    key: 'showroom',
    ordinal: 1,
    defaultLabel: 'Showroom',
    progressive: true,
    terminal: false,
    // Off by default: a stitching-only shop has no showroom step, and this must not appear on
    // the board of every shop that upgrades.
    defaultEnabled: false,
    required: false,
  },
  cutting: {
    key: 'cutting',
    ordinal: 2,
    defaultLabel: 'Cutting',
    progressive: true,
    terminal: false,
    defaultEnabled: true,
    required: false,
  },
  stitching: {
    key: 'stitching',
    ordinal: 3,
    defaultLabel: 'Stitching',
    progressive: true,
    terminal: false,
    defaultEnabled: true,
    required: false,
  },
  handwork: {
    key: 'handwork',
    ordinal: 4,
    defaultLabel: 'Handwork',
    progressive: true,
    terminal: false,
    defaultEnabled: false,
    required: false,
  },
  finishing: {
    key: 'finishing',
    ordinal: 5,
    defaultLabel: 'Finishing',
    progressive: true,
    terminal: false,
    defaultEnabled: true,
    required: false,
  },
  ironing: {
    key: 'ironing',
    ordinal: 6,
    defaultLabel: 'Ironing',
    progressive: true,
    terminal: false,
    defaultEnabled: false,
    required: false,
  },
  qc: {
    key: 'qc',
    ordinal: 7,
    defaultLabel: 'Quality check',
    progressive: true,
    terminal: false,
    defaultEnabled: false,
    required: false,
  },
  ready: {
    key: 'ready',
    ordinal: 8,
    defaultLabel: 'Ready',
    progressive: true,
    terminal: false,
    defaultEnabled: true,
    required: true,
  },
  delivered: {
    key: 'delivered',
    ordinal: 9,
    defaultLabel: 'Delivered',
    progressive: true,
    terminal: true,
    defaultEnabled: true,
    required: true,
  },
  cancelled: {
    key: 'cancelled',
    ordinal: 99,
    defaultLabel: 'Cancelled',
    progressive: false,
    terminal: true,
    defaultEnabled: true,
    required: true,
  },
});

/** Stages a shop cannot switch off. */
export const REQUIRED_STAGES: readonly WorkflowStage[] = Object.freeze(
  WORKFLOW_STAGES.filter((key) => STAGE_DEFINITIONS[key].required),
);

/** Stages enabled for a brand-new shop, in workflow order. */
export const DEFAULT_ENABLED_STAGES: readonly WorkflowStage[] = Object.freeze(
  WORKFLOW_STAGES.filter((key) => STAGE_DEFINITIONS[key].defaultEnabled),
);

/** Stages that mean "no further work will happen on this item". */
export const TERMINAL_STAGES: readonly WorkflowStage[] = Object.freeze(
  WORKFLOW_STAGES.filter((key) => STAGE_DEFINITIONS[key].terminal),
);

export const INITIAL_STAGE: WorkflowStage = 'booked';

/**
 * The stages that are PAID WORK — the axis of a rate card, an assignment and a payout.
 *
 * `booked`, `ready`, `delivered` and `cancelled` are states an item passes through, not work
 * a karigar is paid a piece rate for, so they can never appear on a rate card (task 19), be
 * assigned to someone (task 20) or accrue an earning (task 21).
 */
export const PAYABLE_STAGES = [
  'cutting',
  'stitching',
  'handwork',
  'finishing',
  'qc',
] as const satisfies readonly WorkflowStage[];

export type PayableStage = (typeof PAYABLE_STAGES)[number];

export const payableStageSchema = z.enum(PAYABLE_STAGES);

export function isPayableStage(stage: WorkflowStage): stage is PayableStage {
  return (PAYABLE_STAGES as readonly WorkflowStage[]).includes(stage);
}

export function isWorkflowStage(value: unknown): value is WorkflowStage {
  return typeof value === 'string' && (WORKFLOW_STAGES as readonly string[]).includes(value);
}

export function isTerminalStage(stage: WorkflowStage): boolean {
  return STAGE_DEFINITIONS[stage].terminal;
}

export function isProgressiveStage(stage: WorkflowStage): stage is ProgressiveStage {
  return STAGE_DEFINITIONS[stage].progressive;
}

/**
 * Sort comparator over the canonical ordering. Negative when `a` comes first.
 * `cancelled` sorts after every progressive stage.
 */
export function compareStages(a: WorkflowStage, b: WorkflowStage): number {
  return STAGE_ORDINAL[a] - STAGE_ORDINAL[b];
}

/**
 * The next stage in the progression, restricted to `enabledStages` when given so a shop
 * that hides "Handwork" advances straight from Stitching to Finishing.
 * Returns `undefined` at the end of the line or from a non-progressive stage.
 */
export function nextStage(
  stage: WorkflowStage,
  enabledStages: readonly WorkflowStage[] = STAGE_SEQUENCE,
): WorkflowStage | undefined {
  if (!isProgressiveStage(stage)) return undefined;
  return STAGE_SEQUENCE.find(
    (candidate) =>
      STAGE_ORDINAL[candidate] > STAGE_ORDINAL[stage] && enabledStages.includes(candidate),
  );
}

/** The previous enabled stage in the progression, or `undefined` at the start of the line. */
export function previousStage(
  stage: WorkflowStage,
  enabledStages: readonly WorkflowStage[] = STAGE_SEQUENCE,
): WorkflowStage | undefined {
  if (!isProgressiveStage(stage)) return undefined;
  return [...STAGE_SEQUENCE]
    .reverse()
    .find(
      (candidate) =>
        STAGE_ORDINAL[candidate] < STAGE_ORDINAL[stage] && enabledStages.includes(candidate),
    );
}
